// src/screens/s2/S1Review.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 1 — S1 Review.
// Recap of what Session 1 agreed: freedom age movement, annual gap, the
// standing orders now running, the key dates in the calendar. No charts —
// clean summary cards. Closes with "Today we close the rest."
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../../types/ClientData';
import { formatCurrency, formatAge } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

// Small presentational helper — three-column row used for before/after/delta.
function ComparisonRow({ label, before, after, delta }: { label: string; before: string; after: string; delta: string }) {
  return (
    <div className="grid grid-cols-4 gap-4 py-3 border-b border-gray-100 last:border-0">
      <div className="text-sm text-gray-500 font-medium">{label}</div>
      <div className="text-sm text-gray-600">{before}</div>
      <div className="text-sm font-semibold text-ma-navy">{after}</div>
      <div className="text-sm font-medium text-emerald-700">{delta}</div>
    </div>
  );
}

function StandingOrderCard({ label, amount, recipient }: { label: string; amount: number; recipient: string }) {
  if (amount <= 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{label}</div>
      <div className="text-2xl font-bold text-ma-navy">{formatCurrency(amount)}/mo</div>
      <div className="text-xs text-gray-500 mt-2">→ {recipient}</div>
    </div>
  );
}

function DateCard({ label, value, subtitle }: { label: string; value: string | null; subtitle?: string }) {
  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{label}</div>
      <div className="text-xl font-bold text-ma-navy">{value || '—'}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-2">{subtitle}</div>}
    </div>
  );
}

export default function S1Review({ data }: Props) {
  // ── Freedom age before / after ──────────────────────────────────────────
  const ageBefore = data.s1FreedomAgeBefore ?? data.targetFreedomAge ?? 0;
  const ageAfter = data.targetFreedomAge ?? 0;
  const ageDelta = ageBefore - ageAfter; // positive means brought forward

  // ── Annual gap — uses the stored snapshot + current gap when available ──
  const gapBefore = data.s2GapBefore ?? data.gapAmount ?? 0;
  const gapAfter = data.gapAmount ?? 0;
  const gapClosed = Math.max(0, gapBefore - gapAfter);

  // ── Standing orders agreed in S1 ────────────────────────────────────────
  const pensionMonthly =
    (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);
  const isaMonthly = data.planIsaMonthlyContribution || 0;
  const efMonthly = data.planEfMonthlyTopUp || 0;
  const mortgageOP = data.planMortgageOverpayment || 0;

  // ── Key dates ───────────────────────────────────────────────────────────
  const debtFreeDate: string | null = data.planDebtExtraMonthly && data.planDebtExtraMonthly > 0
    ? 'Snowball plan — see Session 1 debt lever'
    : null;
  const efCompleteDate = data.planEfCompleteDate || null;
  const mortgageFreeDate = data.planMortgagePayoffDate
    || (data.planMortgagePayoffAge ? `Age ${formatAge(data.planMortgagePayoffAge)}` : null);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* HEADER */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-ma-navy">Session 1 Review</h2>
        <p className="text-gray-500 mt-1 text-sm">
          Where you started, where S1 got you, what we close today.
        </p>
      </div>

      {/* BEFORE / AFTER TABLE */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-4">
          The picture, in numbers
        </h3>
        <div className="grid grid-cols-4 gap-4 pb-2 mb-1 border-b-2 border-gray-200">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">&nbsp;</div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Before S1</div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">After S1</div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Change</div>
        </div>
        <ComparisonRow
          label="Freedom age"
          before={ageBefore > 0 ? `Age ${formatAge(ageBefore)}` : '—'}
          after={ageAfter > 0 ? `Age ${formatAge(ageAfter)}` : '—'}
          delta={ageDelta > 0 ? `${ageDelta} year${ageDelta === 1 ? '' : 's'} earlier` : ageDelta < 0 ? `${Math.abs(ageDelta)} year${Math.abs(ageDelta) === 1 ? '' : 's'} later` : 'No change'}
        />
        <ComparisonRow
          label="Annual gap"
          before={gapBefore > 0 ? formatCurrency(gapBefore) : '—'}
          after={gapAfter > 0 ? formatCurrency(gapAfter) : '£0'}
          delta={gapClosed > 0 ? `${formatCurrency(gapClosed)} closed` : 'No change'}
        />
      </div>

      {/* STANDING ORDERS */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
          Standing orders now running
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <StandingOrderCard
            label="Employer pension"
            amount={pensionMonthly}
            recipient={data.pensionProvider || 'Pension provider'}
          />
          <StandingOrderCard
            label="ISA contribution"
            amount={isaMonthly}
            recipient="ISA provider"
          />
          <StandingOrderCard
            label="Emergency fund top-up"
            amount={efMonthly}
            recipient="EF savings account"
          />
          <StandingOrderCard
            label="Mortgage overpayment"
            amount={mortgageOP}
            recipient="Mortgage lender"
          />
        </div>
        {pensionMonthly === 0 && isaMonthly === 0 && efMonthly === 0 && mortgageOP === 0 && (
          <p className="text-sm text-gray-500 mt-4">
            No standing orders recorded from Session 1. Confirm the S1 plan was captured before moving on.
          </p>
        )}
      </div>

      {/* KEY DATES */}
      <div className="mb-8">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
          Key dates in the calendar
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <DateCard
            label="Debt free"
            value={debtFreeDate}
            subtitle={debtFreeDate ? 'Snowball targets smallest debt first' : 'No consumer debt'}
          />
          <DateCard
            label="Emergency fund complete"
            value={efCompleteDate}
            subtitle={efCompleteDate ? `£${efMonthly}/mo until complete` : 'Set on Emergency Fund screen'}
          />
          <DateCard
            label="Mortgage free"
            value={mortgageFreeDate}
            subtitle={mortgageFreeDate && mortgageOP > 0 ? `+£${mortgageOP}/mo overpayment` : ''}
          />
        </div>
      </div>

      {/* CLOSING LINE */}
      <div
        className="rounded-2xl p-8 text-white text-center"
        style={{ background: 'linear-gradient(135deg, #1B2B4B 0%, #2a3f6b 100%)' }}
      >
        <p className="text-sm uppercase tracking-wide opacity-80 mb-2">Today</p>
        <p className="text-3xl font-bold" style={{ color: '#C9A84C' }}>
          We close the rest.
        </p>
        <p className="text-sm opacity-75 mt-3 max-w-lg mx-auto">
          S1 set the personal engine running. S2 adds the business engine —
          employer pension, CT efficiency, cash flow and the profit levers to
          shrink the remaining gap.
        </p>
      </div>
    </div>
  );
}
